import { GameItem, SpeedModifier } from '@/types';
import { getTurboItem } from '@/constants/bonusItems';

export class ItemEffectConverter {
  /**
   * Convert a GameItem speed_boost effect into a SpeedModifier
   */
  static speedBoostToSpeedModifier(
    item: GameItem,
    source?: string
  ): Omit<SpeedModifier, 'id' | 'startTime'> {
    if (item.type !== 'speed_boost') {
      throw new Error(`Cannot convert item type '${item.type}' to speed modifier`);
    }

    // Convert ItemEffect to SpeedModifier format
    // ItemEffect.value should represent the multiplier (e.g., 1.5 for 50% speed increase)
    const multiplier = Math.max(0.1, item.effect.value); // Minimum 10% speed to prevent complete stop
    const duration = item.effect.duration * 1000; // Convert seconds to milliseconds

    return {
      multiplier,
      duration,
      source: source || `speed_boost_item_${item.id}`,
    };
  }

  /**
   * Create shoes item effect (SpeedMode 1 for next round)
   * @param durationSeconds - Duration in seconds (defaults to 12 seconds if not specified)
   */
  static createShoesEffect(durationSeconds: number = 12): Omit<SpeedModifier, 'id' | 'startTime'> {
    return {
      multiplier: 1.25, // SpeedMode 1 is 5.0, SpeedMode 0 is 4.0, so 5.0/4.0 = 1.25
      duration: durationSeconds * 1000, // Convert seconds to milliseconds
      source: 'shoes_item',
    };
  }

  /**
   * Create turbo item effect using configuration from bonus items
   */
  static createTurboEffect(): Omit<SpeedModifier, 'id' | 'startTime'> {
    const turboItem = getTurboItem();
    return {
      multiplier: turboItem.effect.value, // Uses configured value (1.5 = 50% speed increase)
      duration: turboItem.effect.duration * 1000, // Convert seconds to milliseconds
      source: 'turbo_item',
    };
  }

  /**
   * Create a temporary speed boost for testing/development
   */
  static createTestSpeedBoost(
    multiplier: number = 1.5,
    durationSeconds: number = 10,
    source: string = 'test_boost'
  ): Omit<SpeedModifier, 'id' | 'startTime'> {
    return {
      multiplier: Math.max(0.1, multiplier),
      duration: durationSeconds * 1000,
      source,
    };
  }

  /**
   * Create a permanent character trait speed modifier
   */
  static createCharacterTraitModifier(
    speedModeId: number,
    multiplier: number
  ): Omit<SpeedModifier, 'id' | 'startTime'> {
    return {
      multiplier: Math.max(0.1, multiplier),
      duration: 0, // Permanent
      source: `speed_mode_${speedModeId}_trait`,
    };
  }

  /**
   * Validate that a speed modifier is safe to apply
   */
  static validateSpeedModifier(modifier: Partial<SpeedModifier>): boolean {
    // Ensure multiplier is within reasonable bounds
    if (!modifier.multiplier || modifier.multiplier < 0.1 || modifier.multiplier > 5.0) {
      console.warn('Speed modifier multiplier out of safe range (0.1 - 5.0):', modifier.multiplier);
      return false;
    }

    // Ensure duration is non-negative
    if (modifier.duration !== undefined && modifier.duration < 0) {
      console.warn('Speed modifier duration cannot be negative:', modifier.duration);
      return false;
    }

    return true;
  }
}
